<template>
  <div :class="$options.name" :data-e-ga-id="uniqueId" class="tw:flex flex-column tw:gap-1">
    <div v-if="label || $slots.tooltip" class="tw:flex tw:gap-1 tw:items-center">
      <span v-if="label" class="tw:body-1">{{ label }}</span>
      <!--  @slot This slot is for the tooltip component.  -->
      <slot name="tooltip" />
    </div>

    <EdroneTooltip
      :close-trigger-class-name="`close-${$options.name}`"
      :disabled="disabled"
      :offset="[0, 8]"
      custom-theme="edrone-select"
      max-width="auto"
      position="bottom-start"
      type="custom"
      @close="handleCloseSelect"
      @open="handleOpenSelect"
    >
      <div
        ref="selectTrigger"
        :class="[
          `${$options.name}__trigger`,
          classList,
          multiple && Array.isArray(innerSelectedOptions) && innerSelectedOptions.length > 0 ? 'tw:px-1' : 'tw:px-3',
        ]"
        class="tw:flex tw:gap-2 tw:items-center tw:justify-between-3 tw:body-2 position-relative rounded"
        style="height: 2.5rem; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; cursor: pointer"
      >
        <!-- @slot This slot is for the custom selected options content in the trigger. Use `selected` property to display selected items and `remove` to remove them. -->
        <slot
          v-if="
            (Array.isArray(innerSelectedOptions) && innerSelectedOptions.length > 0) ||
            (!Array.isArray(innerSelectedOptions) && innerSelectedOptions !== null)
          "
          :remove="handleRemoveSelect"
          :selected="innerSelectedOptions"
          name="selected"
        >
          <div
            v-if="Array.isArray(innerSelectedOptions)"
            ref="selectedOptionsContainer"
            class="tw:flex tw:gap-1 tw:items-center tw:me-10"
          >
            <template v-if="innerSelectedOptions.length > 5 || isOutSideContainer">
              <EdroneTag
                :text="`${translations.selected}: ${innerSelectedOptions.length}`"
                :disabled="disabled"
                :active="!disabled"
                @click="handleRemoveSelectAll"
              />
            </template>
            <template v-else>
              <EdroneTag
                v-for="(selected, index) in innerSelectedOptions"
                :key="index"
                :disabled="disabled"
                :text="getOptionDisplayName(selected)"
                :active="!disabled"
                @click="handleRemoveSelect(selected as T)"
              />
            </template>
          </div>
          <p v-else class="tw:body-2 tw:my-0 tw:me-10">
            {{ getSelectedOptionName() }}
          </p>
        </slot>
        <template v-else>
          <p class="tw:body-2 tw:my-0 tw:me-10 tw:!text-default-600">{{ placeholder }}</p>
        </template>
        <div
          :class="disabled ? 'tw:bg-default-000' : 'tw:bg-white'"
          class="position-absolute end-0 tw:p-3"
          style="font-size: 1rem"
        >
          <i v-if="loading" class="tw:text-blue-500 fa-light fa-spinner-third fa-spin" />
          <font-awesome-icon v-else-if="invalid" class="tw:text-red-500" icon="fa-light fa-circle-exclamation" />
          <font-awesome-icon v-else icon="fa-light fa-angles-up-down" />
        </div>
      </div>

      <template #custom>
        <div
          :style="`max-height: ${maxHeight}; width: ${selectTriggerWidth}px;`"
          class="tw:flex flex-column tw:overflow-x-hidden"
          :class="sortedOptions.some((item) => hasChildren(item)) ? 'tw:overflow-y-scroll' : 'tw:overflow-y-auto'"
        >
          <div v-if="searchable || taggable" class="tw:p-3 tw:body-2 tw:flex tw:gap-2 tw:items-center flex-nowrap">
            <EdroneCheckbox
              v-if="showCheckboxes && multiple"
              :checked="searchChecked"
              :type="searchType"
              controlled
              @change="handleSelectAllVisible"
            />
            <EdroneInput id="searchInput" :placeholder="translations.search" :value="search" @input="handleSearch">
              <template #icon>
                <font-awesome-icon icon="fa-light fa-magnifying-glass" />
              </template>
            </EdroneInput>
          </div>
          <template v-if="loading">
            <div class="tw:py-10 tw:px-4">
              <EdroneResultState type="loading">
                <h3 class="tw:heading-2">{{ translations.loading }}</h3>
              </EdroneResultState>
            </div>
          </template>
          <template v-else>
            <div v-for="(option, index) in sortedOptions" :key="index">
              <div v-if="hasChildren(option)">
                <div
                  class="tw:p-3 tw:body-2 tw:flex tw:gap-2 tw:items-center tw:cursor-pointer tw:hover:bg-default-000"
                  :class="[isGroupChecked(option) ? 'tw:bg-blue-000 tw:hover:!bg-blue-100' : '']"
                  @click="handleExpandIconClick(option, $event)"
                >
                  <div
                    class="tw:flex tw:items-center tw:gap-2 tw:flex-1 selection-area"
                    :class="[multiple ? 'tw:cursor-pointer' : '', isGroupChecked(option) ? 'tw:text-blue-500' : '']"
                    @click="handleGroupClick(option)"
                  >
                    <EdroneCheckbox
                      v-if="multiple && showCheckboxes"
                      :checked="isGroupChecked(option)"
                      type="indeterminate"
                      controlled
                    />
                    <span class="tw:body-2" :class="isGroupChecked(option) ? 'tw:text-blue-500 tw:font-bold' : ''">
                      {{ getOptionName(option) }}
                    </span>
                  </div>
                  <FontAwesomeIcon
                    :icon="isGroupExpanded(option) ? 'fa-regular fa-caret-up' : 'fa-regular fa-caret-down'"
                    class="tw:cursor-pointer tw:text-black tw:p-1"
                  />
                </div>

                <div v-if="isGroupExpanded(option)">
                  <div
                    v-for="(child, childIndex) in (option as any).children"
                    :key="childIndex"
                    :class="[
                      isSelected(child) ? 'tw:bg-blue-000 tw:hover:!bg-blue-100' : '',
                      multiple ? '' : `close-${$options.name}`,
                      isDisabled(child) ? 'EdroneSelectCustom__option--disabled' : '',
                    ]"
                    class="EdroneSelectCustom__option tw:p-3 tw:pl-10 tw:body-2 tw:flex tw:gap-2 tw:items-center"
                    @click="handleClickSelect(child)"
                  >
                    <EdroneCheckbox v-if="multiple && showCheckboxes" :checked="isSelected(child)" controlled />
                    <!-- @slot This slot is for the custom option content in the dropdown. Use `option` property to display the option and `selected` to check if it's selected. -->
                    <slot :option="child as any" :selected="isSelected(child)" name="option">
                      <span
                        :class="[
                          isSelected(child) ? 'tw:text-blue-500 tw:font-bold' : '',
                          multiple ? '' : `close-${$options.name}`,
                        ]"
                      >
                        {{ getOptionName(child) }}
                      </span>
                    </slot>
                  </div>
                </div>
              </div>

              <div
                v-else
                :class="[
                  isSelected(option) ? 'EdroneSelectCustom__option--active' : '',
                  multiple ? '' : `close-${$options.name}`,
                  isDisabled(option) ? 'EdroneSelectCustom__option--disabled' : '',
                ]"
                class="EdroneSelectCustom__option tw:p-3 tw:body-2 tw:flex tw:gap-2 tw:items-center"
                @click="handleClickSelect(option)"
              >
                <EdroneCheckbox v-if="multiple && showCheckboxes" :checked="isSelected(option)" controlled />
                <!-- @slot This slot is for the custom option content in the dropdown. Use `option` property to display the option and `selected` to check if it's selected. -->
                <slot :option="option as any" :selected="isSelected(option)" name="option">
                  <span
                    :class="[
                      isSelected(option) ? 'tw:text-blue-500 tw:font-bold' : '',
                      multiple ? '' : `close-${$options.name}`,
                    ]"
                  >
                    {{ getOptionName(option) }}
                  </span>
                </slot>
              </div>
            </div>
            <div
              v-if="newOption"
              :class="[multiple ? '' : `close-${$options.name}`]"
              class="tw:p-3 tw:body-2 tw:flex tw:gap-2 tw:items-center cursor-pointer tw:!text-blue-500"
              style="cursor: pointer"
              @click="handleClickAddOption(newOption)"
            >
              <font-awesome-icon icon="fa-light fa-plus" />
              {{ translations.add }} "{{ newOption }}"
            </div>
            <div v-else-if="search && (!sortedOptions || !sortedOptions.length)" class="tw:py-10 tw:px-4">
              <EdroneResultState type="empty">
                <h3 class="tw:heading-2 tw:mb-1">{{ translations.empty.title }} "{{ search }}"</h3>
                <p class="tw:body-2 tw:!text-default-700">{{ translations.empty.desc }}</p>
              </EdroneResultState>
            </div>
          </template>
        </div>
      </template>
    </EdroneTooltip>

    <div v-if="showInfoContainer">
      <div v-if="!disabled && invalid && errorMsg" class="tw:caption tw:!text-red-500">
        {{ errorMsg }}
      </div>
      <div v-else-if="helper" class="tw:caption tw:!text-default-700">
        {{ helper }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: "EdroneSelectCustom",
  customOptions: {},
};
</script>

<script generic="T" lang="ts" setup>
import { computed, ref, watch, Ref, nextTick, unref, onMounted } from "vue";
import type { ComputedRef, UnwrapRef } from "vue";
import EdroneTooltip from "./EdroneTooltip.vue";
import EdroneInput from "./EdroneInput.vue";
import EdroneCheckbox from "./EdroneCheckbox.vue";
import EdroneTag from "./EdroneTag.vue";
import EdroneResultState from "edrone-components/EdroneResultState.vue";
import _ from "lodash";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

type Props = {
  /**
   * Options to select from. Can be an array of strings, numbers or objects
   */
  options: ComputedRef<T[]> | T[];
  /**
   * Selected option(s). Can be a single value or an array of values (depending on the `multiple` prop)
   */
  selectedOptions: ComputedRef<T> | ComputedRef<T[]> | Ref<T> | T | T[] | null;
  /**
   * Label of the select
   */
  label?: string;
  /**
   * Placeholder of the select
   */
  placeholder?: string;
  /**
   * Unique ID of the select
   */
  uniqueId?: string;
  /**
   * If true, the select will be marked as invalid
   */
  invalid?: boolean;
  /**
   * If true, the select will be disabled
   */
  disabled?: boolean;
  /**
   * Error message to display when the select is invalid
   */
  errorMsg?: string;
  /**
   * Helper text to display below the select
   */
  helper?: string;
  /**
   * If true, the select will be in loading state
   */
  loading?: boolean;
  /**
   * If true, the user will be able to select multiple options
   */
  multiple?: boolean;
  /**
   * If true, the user will be able to add a new option. The event `add` will be emitted with the value of the input
   */
  taggable?: boolean;
  /**
   * If true, the user will be able to select an empty option
   */
  allowEmpty?: boolean;
  /**
   * If true, the user will be able to search for options
   */
  searchable?: boolean;
  /**
   * The property of the option object to search by
   */
  searchBy?: string;
  /**
   * If true, the selected options will be sorted to the top
   */
  sortable?: boolean;
  /**
   * The maximum height of the select options container
   */
  maxHeight?: string;
  /**
   * If true, checkboxes will be displayed next to the options for `multiple` select
   */
  showCheckboxes?: boolean;
  /**
   * If true, the select will be controlled
   */
  controlled?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  label: "",
  placeholder: "",
  uniqueId: "edrone-select-custom",
  invalid: false,
  disabled: false,
  errorMsg: "",
  helper: "",
  loading: false,
  multiple: false,
  taggable: false,
  allowEmpty: true,
  searchable: false,
  searchBy: "name",
  sortable: false,
  maxHeight: "50vh",
  showCheckboxes: true,
});

type Events = {
  /**
   * Emits when an option is selected. The selected option is emitted as an argument
   */
  (event: "select", option: T): void;
  /**
   * Emits when the selected option(s) change (on close of the select or when an option is removed while the select is closed). The new selected option(s) are emitted as an argument
   */
  (event: "change", selectedOptions: T | T[] | null): void;
  /**
   * Emits when the select is opened
   */
  (event: "open"): void;
  /**
   * Emits when the select is closed
   */
  (event: "close"): void;
  /**
   * Emits when a new option is added (only when `taggable` prop is true). The value of the input is emitted as an argument
   */
  (event: "add", value: string): void;
  /**
   * Emits when an option is removed. The removed option is emitted as an argument
   */
  (event: "remove", option: T): void;
  /**
   * Emits when the search input value changes. The new value is emitted as an argument
   */
  (event: "search", value: string): void;
};

const emit = defineEmits<Events>();
const translations = window._trans?.edroneSelectTranslations || {
  selected: "Selected items",
  search: "Search options",
  add: "Add",
  empty: {
    title: "No results for",
    desc: "Check spelling or use broader terms.",
  },
  loading: "Searching for results...",
};

const isOpen = ref(false);

const selectTrigger = ref<HTMLInputElement | null>(null);
const selectTriggerWidth = ref<number | undefined>(undefined);

const showInfoContainer = computed(() => {
  return props.helper || (!props.disabled && props.invalid && props.errorMsg);
});

const classList = computed(() => {
  return {
    "EdroneSelectCustom--active": isOpen.value,
    "EdroneSelectCustom--invalid": props.invalid,
    "EdroneSelectCustom--disabled": props.disabled,
  };
});

const innerSelectedOptions = ref<T[] | T | null>(unref(props.selectedOptions));
const sortedOptions = ref<T[]>(unref(props.options));
const newOption = ref<string>("");
const search = ref<string>("");

const searchChecked = ref<boolean>(false);
const searchType = ref<"default" | "indeterminate">("default");

const expandedGroups = ref<Set<any>>(new Set());

watch(
  () => props.selectedOptions,
  (selectedOptions) => {
    if (!_.isEqual(selectedOptions, innerSelectedOptions.value)) {
      innerSelectedOptions.value = unref(selectedOptions);
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => props.options,
  (newValue) => {
    sortedOptions.value = getFilteredOptions(unref(newValue));
  },
  { deep: true }
);

function handleOpenSelect() {
  isOpen.value = true;
  emit("open");
  gtagUniversalSend({ component_id: props.uniqueId + "-open" });

  selectTriggerWidth.value = selectTrigger.value?.offsetWidth;
  search.value = "";
  newOption.value = "";

  if (props.sortable) {
    sortedOptions.value = getFilteredOptions(unref(props.options)).sort(sortBySelected);
  } else {
    sortedOptions.value = getFilteredOptions(unref(props.options));
  }

  if (props.searchable || props.taggable) {
    nextTick(() => {
      const searchFocus = document.querySelector("#searchInput input") as HTMLElement;
      searchFocus?.focus();
    });
  }
}

function handleCloseSelect() {
  isOpen.value = false;
  emit("close");

  emit("change", JSON.parse(JSON.stringify(innerSelectedOptions.value)));
}

function handleClickSelect(option) {
  if (isSelected(option)) {
    handleRemoveSelect(option);
    return;
  }

  emit("select", JSON.parse(JSON.stringify(option)));
  gtagUniversalSend({ component_id: props.uniqueId + "-select" });

  if (props.multiple) {
    if (Array.isArray(innerSelectedOptions.value)) {
      props.controlled ? (innerSelectedOptions.value = props.selectedOptions) : innerSelectedOptions.value.push(option);
    } else {
      props.controlled ? (innerSelectedOptions.value = props.selectedOptions) : (innerSelectedOptions.value = [option]);
    }
  } else {
    props.controlled ? (innerSelectedOptions.value = props.selectedOptions) : (innerSelectedOptions.value = option);
  }
}

function handleClickAddOption(value: string) {
  emit("add", value);
  gtagUniversalSend({ component_id: props.uniqueId + "-add" });

  search.value = "";
  newOption.value = "";
}

function handleRemoveSelect(option: T) {
  if (props.disabled) {
    return;
  }

  emit("remove", JSON.parse(JSON.stringify(option)));
  gtagUniversalSend({ component_id: props.uniqueId + "-remove" });

  if (props.multiple) {
    if (Array.isArray(innerSelectedOptions.value) && (props.allowEmpty || innerSelectedOptions.value.length > 1)) {
      props.controlled
        ? (innerSelectedOptions.value = props.selectedOptions)
        : (innerSelectedOptions.value = innerSelectedOptions.value.filter((selected) => {
            return !_.isEqual(selected, option);
          }));
    }
  } else if (props.allowEmpty) {
    props.controlled ? (innerSelectedOptions.value = props.selectedOptions) : (innerSelectedOptions.value = null);
  }

  if (!isOpen.value) {
    emit("change", JSON.parse(JSON.stringify(innerSelectedOptions.value)));
  }
}

function handleRemoveSelectAll() {
  if (props.disabled || innerSelectedOptions.value === null) {
    return;
  }
  if (Array.isArray(innerSelectedOptions.value)) {
    innerSelectedOptions.value.forEach((option) => {
      emit("remove", JSON.parse(JSON.stringify(option)));
    });
  } else {
    emit("remove", JSON.parse(JSON.stringify(innerSelectedOptions.value)));
  }

  innerSelectedOptions.value = [];
  if (!isOpen.value) {
    emit("change", JSON.parse(JSON.stringify(innerSelectedOptions.value)));
  }
}

function handleSearch(value: string) {
  search.value = value;
  emit("search", value);
  gtagUniversalSend({ component_id: props.uniqueId + "-search" });
  sortedOptions.value = getFilteredOptions(unref(props.options));

  if (props.taggable) {
    if (!sortedOptions.value.find((option) => _.isEqual(option, value))) {
      newOption.value = value;
    } else {
      newOption.value = "";
    }
  }
}

function getFilteredOptions(array: T[]): T[] {
  return array
    .map((option) => {
      if (hasChildren(option)) {
        const groupMatches =
          (typeof option === "object" &&
            option !== null &&
            props.searchBy in option &&
            (option as any)[props.searchBy]?.toString().toLowerCase().includes(search.value.toLowerCase())) ||
          (option as any)?.toString().toLowerCase().includes(search.value.toLowerCase());

        const filteredChildren = (option as any).children.filter(
          (child: any) =>
            (typeof child === "object" &&
              child !== null &&
              props.searchBy in child &&
              child[props.searchBy]?.toString().toLowerCase().includes(search.value.toLowerCase())) ||
            (child as any)?.toString().toLowerCase().includes(search.value.toLowerCase())
        );

        if (groupMatches || filteredChildren.length > 0) {
          return {
            ...option,
            children: groupMatches ? (option as any).children : filteredChildren,
          };
        }

        return null;
      }

      const matches =
        (typeof option === "object" &&
          option !== null &&
          props.searchBy in option &&
          (option as any)[props.searchBy]?.toString().toLowerCase().includes(search.value.toLowerCase())) ||
        (option as any)?.toString().toLowerCase().includes(search.value.toLowerCase());

      return matches ? option : null;
    })
    .filter((option) => option !== null);
}

function sortBySelected(a: any, b: any) {
  if (isSelected(a) && !isSelected(b)) {
    return -1;
  }
  if (!isSelected(a) && isSelected(b)) {
    return 1;
  }
  return 0;
}

function isOptionWithValue<T extends object>(option: T | null): option is T & { value: number | string } {
  return option !== null && typeof option === "object" && "value" in option;
}

function isOptionWithName<T extends object>(option: T | null): option is T & { name: number | string } {
  return option !== null && typeof option === "object" && "name" in option;
}

function isSelected(option: any): boolean {
  if (!props.multiple && hasChildren(option)) {
    return false;
  }

  if (props.multiple && Array.isArray(innerSelectedOptions.value)) {
    if (option && isOptionWithValue(option)) {
      return !!innerSelectedOptions.value.find(
        (selectedOption) => isOptionWithValue(selectedOption) && selectedOption.value === option.value
      );
    }
    return !!innerSelectedOptions.value.find((selectedOption) => _.isEqual(selectedOption, option));
  } else {
    if (option && isOptionWithValue(option)) {
      return (
        !!innerSelectedOptions.value &&
        isOptionWithValue(innerSelectedOptions.value) &&
        innerSelectedOptions.value.value === option.value
      );
    }
    return !!innerSelectedOptions.value && _.isEqual(innerSelectedOptions.value, option);
  }
}

function isDisabled(option: any) {
  return option.disabled === true;
}

function getOptionName<T>(option: T): string {
  if (option && isOptionWithName(option)) {
    return typeof option.name === "string" ? option.name : String(option);
  }
  return String(option);
}

function getSelectedOptionName(): string | T | undefined {
  const selectedOptions = innerSelectedOptions.value as UnwrapRef<T>;
  return getOptionDisplayName(selectedOptions);
}

watch(
  () => [innerSelectedOptions.value, search.value, sortedOptions.value],
  () => {
    if (
      innerSelectedOptions.value &&
      Array.isArray(innerSelectedOptions.value) &&
      innerSelectedOptions.value.length > 0
    ) {
      searchType.value = allVisibleOptionsAreSelected() ? "default" : "indeterminate";
    }
    searchChecked.value =
      (Array.isArray(innerSelectedOptions.value) && innerSelectedOptions.value.length > 0) ||
      (!Array.isArray(innerSelectedOptions.value) && innerSelectedOptions.value !== null);
  },
  { deep: true, immediate: true }
);

function allVisibleOptionsAreSelected(): boolean {
  return sortedOptions.value.every((option) => isSelected(option));
}

function handleSelectAllVisible() {
  if (searchChecked.value) {
    handleRemoveSelectAll();
  } else {
    sortedOptions.value.forEach((option) => {
      if (!isSelected(option)) {
        handleClickSelect(option);
      }
    });
  }
}

const isOutSideContainer = ref(false);
const selectedOptionsContainer = ref<HTMLDivElement | null>(null);
watch(
  innerSelectedOptions,
  async () => {
    isOutSideContainer.value = false;
    await nextTick();
    const containerWidth = selectedOptionsContainer.value?.clientWidth || 0;
    const triggerWidth = selectTrigger.value?.clientWidth || 0;
    isOutSideContainer.value = containerWidth > triggerWidth - 40;
  },
  { deep: true, immediate: true }
);

function hasChildren(option: any): option is any & { children: any[] } {
  return (
    option &&
    typeof option === "object" &&
    "children" in option &&
    Array.isArray(option.children) &&
    option.children.length > 0
  );
}

onMounted(() => {
  const optionsArray = unref(props.options);
  optionsArray.forEach((option: any) => {
    if (hasChildren(option)) {
      if (!props.multiple) {
        expandedGroups.value.add(option.value);
      }
    }
  });
});

function isGroupExpanded(option: any): boolean {
  return expandedGroups.value.has(option.value);
}

function toggleGroupExpansion(option: any): void {
  if (expandedGroups.value.has(option.value)) {
    expandedGroups.value.delete(option.value);
  } else {
    expandedGroups.value.add(option.value);
  }
}

function isGroupSelected(option: any): boolean {
  return hasChildren(option) && option.children.every((child: any) => isSelected(child));
}

function isGroupPartiallySelected(option: any): boolean {
  if (!hasChildren(option)) return false;

  const someSelected = option.children.some((child: any) => isSelected(child));

  const allSelected = option.children.every((child: any) => isSelected(child));

  return someSelected && !allSelected;
}

function isGroupChecked(option: any): boolean {
  return isGroupSelected(option) || isGroupPartiallySelected(option);
}

function toggleGroupSelection(option: any): void {
  if (!hasChildren(option)) return;

  const someSelected = option.children.some((child: any) => isSelected(child));

  option.children.forEach((child: any) => {
    const childSelected = isSelected(child);

    if (someSelected) {
      if (childSelected) {
        handleRemoveSelect(child);
      }
    } else {
      if (!childSelected) {
        handleClickSelect(child);
      }
    }
  });
}

function handleExpandIconClick(option: any, event: Event): void {
  event.stopPropagation();
  toggleGroupExpansion(option);
}

function handleGroupClick(option: any): void {
  if (props.multiple) {
    toggleGroupSelection(option);
  }
}

function findParentOption(childOption: any): any | null {
  const optionsArray = unref(props.options);

  for (const option of optionsArray) {
    if (hasChildren(option)) {
      const foundChild = (option as any).children.find(
        (child: any) => _.isEqual(child, childOption) || child.value === childOption.value
      );
      if (foundChild) {
        return option;
      }
    }
  }

  return null;
}

function getOptionDisplayName(option: any): string {
  const parentOption = findParentOption(option);

  if (parentOption) {
    return `${getOptionName(parentOption)}: ${getOptionName(option)}`;
  }

  return getOptionName(option);
}
</script>
<style scoped>
@reference "vue-app/assets/scss/main.css";

.EdroneSelectCustom {
  font-family: "Roboto", sans-serif;
}

.EdroneSelectCustom__trigger {
  @apply tw:bg-white tw:border tw:border-default-200;
}

.EdroneSelectCustom__trigger:hover {
  @apply tw:border-default-400;
}

.EdroneSelectCustom__option {
  cursor: pointer;
}

.EdroneSelectCustom__option:hover {
  @apply tw:bg-default-000;
}

.EdroneSelectCustom__option--active {
  @apply tw:bg-blue-000 tw:text-blue-500;
}

.EdroneSelectCustom__option--active:hover {
  @apply tw:bg-blue-100 tw:text-blue-500;
}

.EdroneSelectCustom__option--invalid {
  @apply tw:text-red-500;
}

.EdroneSelectCustom__option--invalid:hover {
  @apply tw:text-red-500 tw:bg-red-000;
}

.EdroneSelectCustom__option--disabled {
  cursor: not-allowed;
  pointer-events: none;
  @apply tw:text-default-500;
}

.EdroneSelectCustom .EdroneSelectCustom--active {
  @apply tw:border-blue-500;
}

.EdroneSelectCustom .EdroneSelectCustom--invalid {
  @apply tw:border-red-500;
}

.EdroneSelectCustom .EdroneSelectCustom--disabled {
  @apply tw:bg-default-000 tw:border-default-000;
}

.EdroneSelectCustom .EdroneTooltip > div,
.EdroneSelectCustom > .EdroneTooltip {
  width: 100%;
}
</style>
<style>
.tippy-box[data-theme~="edrone-select"] {
  background-color: #ffffff;
  box-shadow: 4px 4px 24px rgba(0, 0, 0, 0.22);
}

.tippy-box[data-theme~="edrone-select"] .tippy-content {
  padding: 0;
  background: #ffffff;
}
</style>
